<template>
  <div>
    <v-dialog
      v-model="show"
      max-width="1000"
    >
      <v-card v-if="projectID">
        <v-card-title>
          Rate history
          <v-spacer></v-spacer>
          <v-btn icon @click="show = !show">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-data-table
            dense
            :headers="projectRateHeaders"
            :items="projectRateHistory"
            :items-per-page="historyItemsPerPage"
            item-key="Project_Rate_ID"
            class="elevation-1"
            show-expand
            :expanded.sync="expanded"
            :calculate-widths="false"
            :mobile-breakpoint="400"
          >
            <!--Nested Table-->
            <template v-slot:expanded-item="{ headers, item }">
              <td :style="{ width : calculateWidth + 'px'}" class="px-0" :colspan="headers.length">
                <v-data-table
                  style="border-radius: 0"
                  dense
                  class="elevation-0"
                  :headers="employeeRateHeaders"
                  :items="item.EmployeeRate"
                  item-key="Rate_ID"
                  :calculate-widths="false"
                  :mobile-breakpoint="400"
                >
                  <template v-slot:[`item.From_Date`]="{ item: nestedItem }">
                    <DatePicker
                      v-if="editEmpRateMode && currentEmployeeRate.Rate_ID === nestedItem.Rate_ID"
                      v-model="currentEmployeeRate.From_Date"
                      dense
                      :min="minDateEmployeeRate"
                      :max="maxDateEmployeeRate"
                    />
                    <span v-else>{{ nestedItem.From_Date }}</span>
                  </template>
                  <template v-slot:[`item.Full_Name`]="{ item: nestedItem }">
                    <v-select
                      v-if="editEmpRateMode && currentEmployeeRate.Rate_ID === nestedItem.Rate_ID"
                      dense
                      hide-details
                      v-model="currentEmployeeRate.Employee_ID"
                      :items="employeeList"
                      item-text="full_name"
                      item-value="employee_id"
                    >
                      <template v-slot:prepend-item>
                        <v-container>
                          <v-row>
                            <v-col>
                              <v-text-field
                                dense
                                hide-details
                                label="Search employee"
                                v-model="searchEmployee"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-container>
                      </template>
                    </v-select>
                    <span v-else>{{ nestedItem.Full_Name }}</span>
                  </template>
                  <template v-slot:[`item.Rate`]="{ item: nestedItem }">
                    <v-text-field
                      v-if="editEmpRateMode && currentEmployeeRate.Rate_ID === nestedItem.Rate_ID"
                      dense
                      hide-details
                      v-model="currentEmployeeRate.Rate"
                      :rules="[rules.required]"
                      type="number"
                    ></v-text-field>
                    <span v-else>{{ nestedItem.Rate }}</span>
                  </template>
                  <template v-slot:[`item.edit`]="{ item: nestedItem }">
                    <v-tooltip
                      bottom
                      :color="editEmpRateMode && currentEmployeeRate.Rate_ID === nestedItem.Rate_ID ? 'success' : 'primary'"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          :disabled="
                          (editEmpRateMode && currentEmployeeRate.Rate_ID !== nestedItem.Rate_ID) ||
                          canSaveEmployeeRate || editProjectRateMode || addProjectRateMode"
                          small
                          v-bind="attrs"
                          v-on="on"
                          icon
                          :color="editEmpRateMode && currentEmployeeRate.Rate_ID === nestedItem.Rate_ID ? 'success' : 'primary'"
                          @click="onEditEmpRateClick(item, nestedItem)"
                        >
                          <v-icon>
                            {{
                              editEmpRateMode && currentEmployeeRate.Rate_ID === nestedItem.Rate_ID ? 'mdi-check-circle' : 'mdi-pencil-circle'
                            }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ editEmpRateMode ? 'Save' : 'Edit' }}</span>
                    </v-tooltip>
                    <v-tooltip
                      bottom color="red darken-2"
                      v-if="currentEmployeeRate?.Rate_ID && currentEmployeeRate.Rate_ID !== nestedItem.Rate_ID || !editEmpRateMode"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          :disabled="editEmpRateMode || editProjectRateMode || addProjectRateMode"
                          small
                          v-bind="attrs"
                          v-on="on"
                          icon color="red darken-2"
                          @click="openEmployeeRateDeleteDialog(item, nestedItem)"
                        >
                          <v-icon>mdi-delete-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>Delete</span>
                    </v-tooltip>
                    <v-tooltip
                      bottom color="primary"
                      v-if="editEmpRateMode && currentEmployeeRate.Rate_ID === nestedItem.Rate_ID"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          small
                          v-bind="attrs"
                          v-on="on"
                          icon color="primary"
                          @click="onCancelEmpRateEdit"
                        >
                          <v-icon>mdi-close-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>Cancel</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </td>
            </template>
            <!--/Nested Table -->
            <template v-slot:[`item.From_Date`]="{ item }">
              <DatePicker
                v-if="editProjectRateMode && currentHistoryRate.Project_Rate_ID === item.Project_Rate_ID"
                v-model="currentHistoryRate.From_Date"
                dense
                :min="minDateProjectRateEdit"
                :max="maxDateProjectRateEdit"
              />
              <span v-else>{{ item.From_Date }}</span>
            </template>
            <template v-slot:[`item.Rate`]="{ item }">
              <v-text-field
                v-if="editProjectRateMode && currentHistoryRate.Project_Rate_ID === item.Project_Rate_ID"
                dense
                hide-details
                type="number"
                v-model="currentHistoryRate.Rate"
                :rules="[rules.required]"
              ></v-text-field>
              <span v-else>{{ item.Rate }}</span>
            </template>
            <template v-slot:[`item.edit`]="{ item }">
              <v-tooltip
                bottom
                :color="editProjectRateMode && currentHistoryRate.Project_Rate_ID === item.Project_Rate_ID ? 'success' : 'primary'"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :disabled="addProjectRateMode || (editProjectRateMode && currentHistoryRate.Project_Rate_ID !== item.Project_Rate_ID) || canSaveProjectRate || editEmpRateMode"
                    small
                    v-bind="attrs"
                    v-on="on"
                    icon
                    :color="editProjectRateMode && currentHistoryRate.Project_Rate_ID === item.Project_Rate_ID ? 'success' : 'primary'"
                    @click="onEditProjRateClick(item)"
                  >
                    <v-icon>
                      {{
                        editProjectRateMode && currentHistoryRate.Project_Rate_ID === item.Project_Rate_ID ? 'mdi-check-circle' : 'mdi-pencil-circle'
                      }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ editProjectRateMode ? 'Save' : 'Edit' }}</span>
              </v-tooltip>
              <v-tooltip
                bottom color="red darken-2"
                v-if="!editProjectRateMode || (editProjectRateMode && currentHistoryRate.Project_Rate_ID !== item.Project_Rate_ID) && canDelete"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    :disabled="addProjectRateMode || editProjectRateMode || editEmpRateMode"
                    v-bind="attrs"
                    v-on="on"
                    icon color="red darken-2"
                    @click="openProjectRateDeleteDialog(item)"
                  >
                    <v-icon>mdi-delete-circle</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
              <v-tooltip
                bottom color="primary"
                v-if="editProjectRateMode && currentHistoryRate.Project_Rate_ID === item.Project_Rate_ID"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    v-bind="attrs"
                    v-on="on"
                    icon color="primary"
                    @click="cancelProjectRateEdit"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
              <v-tooltip bottom color="success">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :disabled="editEmpRateMode || editProjectRateMode || addProjectRateMode"
                    small
                    v-bind="attrs"
                    v-on="on"
                    icon color="success"
                    @click="addNewEmployeeRate(item)"
                  >
                    <v-icon>mdi-plus-circle</v-icon>
                  </v-btn>
                </template>
                <span>Add</span>
              </v-tooltip>
            </template>
            <template v-slot:[`body.append`] v-if="canEdit && !notFromProjectsModule">
              <td
                class="text-center px-2 text-no-wrap"
              >
              </td>
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addProjectRateMode}"
              >
                <DatePicker
                  v-if="addProjectRateMode"
                  v-model="currentHistoryRate.From_Date"
                  :min="minDateProjectRateAdd"
                  dense
                />
              </td>
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addProjectRateMode}"
              >
                <v-text-field
                  v-if="addProjectRateMode"
                  dense
                  hide-details
                  type="number"
                  v-model="currentHistoryRate.Rate"
                  :rules="[rules.required]"
                ></v-text-field>
              </td>
              <td class="text-center px-2 text-no-wrap">
                <v-tooltip bottom color="success">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      icon
                      :disabled="(editProjectRateMode || editEmpRateMode || addProjectRateMode && canSaveProjectRate)"
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                      @click="addProjectRate"
                    >
                      <v-icon>{{ addProjectRateMode ? 'mdi-check-circle' : 'mdi-plus-circle' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ addProjectRateMode ? 'Save' : 'Add' }}</span>
                </v-tooltip>
                <v-tooltip bottom color="primary" v-if="addProjectRateMode">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      icon
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="cancelProjectRateAdd"
                    >
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>Cancel</span>
                </v-tooltip>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="removeProjectRateDialog"
      v-model="removeProjectRateDialog"
      max-width="450"
      @click:outside="cancelProjectRateDelete"
      @keydown.esc="cancelProjectRateDelete"
    >
      <v-card>
        <v-card-title>
          Delete
          <v-spacer></v-spacer>
          <v-btn icon @click="cancelProjectRateDelete">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <span>Are you sure you want to delete this current rate?</span>
          <span v-if="currentHistoryRate.EmployeeRate.length && projectRateHistory.length > 1">
            <br> You also have the option to delete all employee rates
            associated with this project rate.
          </span>
          <span><br> Delete action is irreversible.</span>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            outlined
            small
            @click="cancelProjectRateDelete"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="currentHistoryRate.EmployeeRate.length"
            color="red darken-2"
            small
            class="white--text"
            @click="deleteProjectRateHistory(true)"
          >
            delete with employee rates
          </v-btn>
          <v-btn
            v-if="(projectRateHistory[projectRateHistory.length -1].Project_Rate_ID !== currentHistoryRate.Project_Rate_ID) || (projectRateHistory[projectRateHistory.length -1].Project_Rate_ID === currentHistoryRate.Project_Rate_ID && !currentHistoryRate.EmployeeRate.length)"
            color="red darken-2"
            small
            class="white--text"
            @click="deleteProjectRateHistory(false)"
          >
            delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="confirmRateCopyDialog"
      v-model="confirmRateCopyDialog"
      max-width="460"
      @click:outside="cancelProjectRateAdd"
      @keydown.esc="cancelProjectRateAdd"
    >
      <v-card>
        <v-card-title>
          Confirmation
          <v-spacer></v-spacer>
          <v-btn icon @click="cancelProjectRateAdd">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <span>The previous project rate range included individual employee rates.</span>
          <span><br> Would you like to include them in the new range, or save only the project rate?</span>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            outlined
            small
            @click="cancelProjectRateAdd"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            small
            @click="postProjectRate(true)"
          >
            save with employee rates
          </v-btn>
          <v-btn
            color="primary"
            small
            @click="postProjectRate(false)"
          >
            save rate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="removeEmployeeRateDialog"
      v-model="removeEmployeeRateDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'You can\'t restore data after delete'"
      @onDeleteClick="deleteEmployeeRate"
      @onCancelClick="cancelDeleteEmployeeRate"
      @onCloseClick="cancelDeleteEmployeeRate"
    />
  </div>
</template>
<script>
import moment from 'moment'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'
export default {
  name: 'ProjectRate',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      default: false
    },
    projectID: {
      required: false,
      default: null
    },
    minDate: {
      required: false,
      default: null
    },
    notFromProjectsModule: {
      required: false,
      default: false
    }
  },
  data () {
    return {
      addNewEmpRateMode: false,
      addProjectRateMode: false,
      allEmployeeRate: [],
      currentHistoryRate: null,
      confirmRateCopyDialog: false,
      currentEmployeeRate: null,
      editProjectRateMode: false,
      expanded: [],
      editEmpRateMode: false,
      historyItemsPerPage: 5,
      nextProjectRate: null,
      projectRateHistory: [],
      previousProjectRate: null,
      removeEmployeeRateDialog: false,
      removeProjectRateDialog: false,
      rules: {
        required: v => !!v || 'Required'
      },
      searchEmployee: ''
    }
  },
  async mounted () {
    await this.getPermissions()
    this.getEmployeesRate()
  },
  computed: {
    projectRateHeaders () {
      const headers = [
        {
          text: '',
          width: 100,
          value: 'data-table-expand'
        },
        {
          text: 'From Date',
          value: 'From_Date',
          width: 400,
          divider: true,
          align: 'left',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap primary--text text--darken-2 font-weight-medium'
        },
        {
          text: 'Rate',
          width: 100,
          value: 'Rate',
          align: 'left',
          divider: this.canEdit && !this.notFromProjectsModule,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap primary--text text--darken-2 font-weight-medium'
        }
      ]
      if (this.canEdit && !this.notFromProjectsModule) {
        headers.push({
          text: '#',
          width: 140,
          value: 'edit',
          align: 'center',
          sortable: false,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        })
      }
      return headers
    },
    employeeRateHeaders () {
      const employeeRateHeaders = [
        {
          text: '',
          sortable: false,
          width: 100,
          value: 'data-table-expand'
        },
        {
          text: 'From Date',
          value: 'From_Date',
          width: 200,
          divider: true,
          align: 'left',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Employee',
          value: 'Full_Name',
          width: 200,
          divider: true,
          align: 'left',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Rate',
          width: 100,
          value: 'Rate',
          align: 'left',
          divider: this.canEdit && !this.notFromProjectsModule,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        }
      ]
      if (this.canEdit && !this.notFromProjectsModule) {
        employeeRateHeaders.push({
          text: '#',
          width: 140,
          value: 'edit',
          align: 'center',
          sortable: false,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        })
      }
      return employeeRateHeaders
    },
    calculateWidth () {
      return this.projectRateHeaders.reduce((acc, next) => {
        return acc + next.width
      }, 0)
    },
    canSaveEmployeeRate () {
      return this.editEmpRateMode && (!this.currentEmployeeRate.From_Date ||
        !this.currentEmployeeRate.Employee_ID || !this.currentEmployeeRate.Rate)
    },
    canSaveProjectRate () {
      return (this.addProjectRateMode || this.editProjectRateMode) && (!this.currentHistoryRate || !this.currentHistoryRate.From_Date ||
        !this.currentHistoryRate.Rate)
    },
    employeeList () {
      const employeeList = this.$store.getters.getEmployee
      if (this.searchEmployee) {
        const reg = new RegExp(this.searchEmployee.toLowerCase(), 'gi')
        return employeeList.filter(item => {
          reg.lastIndex = 0
          return reg.test(item.full_name.toLowerCase())
        })
      }
      return employeeList
    },
    minDateProjectRateAdd () {
      let fromDate
      if (this.previousProjectRate && this.previousProjectRate.EmployeeRate.length) {
        fromDate = this.previousProjectRate.EmployeeRate[0].From_Date
      } else if (this.previousProjectRate && !this.previousProjectRate.EmployeeRate.length) {
        fromDate = this.previousProjectRate.From_Date
      }
      return fromDate ? moment(fromDate).add(1, 'day').format('YYYY-MM-DD') : this.minDate
    },
    minDateProjectRateEdit () {
      let minDate
      if (this.nextProjectRate && this.nextProjectRate.EmployeeRate.length) {
        minDate = this.nextProjectRate.EmployeeRate[0].From_Date
      } else if (this.nextProjectRate && !this.nextProjectRate.EmployeeRate.length) {
        minDate = this.nextProjectRate.From_Date
      }
      return minDate ? moment(minDate).add(1, 'day').format('YYYY-MM-DD') : this.minDate
    },
    maxDateProjectRateEdit () {
      let maxDate
      if (this.previousProjectRate && this.previousProjectRate.EmployeeRate.length) {
        maxDate = this.previousProjectRate.EmployeeRate[this.previousProjectRate.EmployeeRate.length - 1].From_Date
      } else if (this.previousProjectRate && !this.previousProjectRate.EmployeeRate.length) {
        maxDate = this.previousProjectRate.From_Date
      }
      return maxDate ? moment(maxDate).subtract(1, 'day').format('YYYY-MM-DD') : null
    },
    minDateEmployeeRate () {
      return this.currentHistoryRate.From_Date
    },
    maxDateEmployeeRate () {
      const index = this.projectRateHistory.findIndex(el => el.Project_Rate_ID === this.currentHistoryRate.Project_Rate_ID)
      return this.projectRateHistory[index - 1] ? moment(this.projectRateHistory[index - 1].From_Date).subtract(1, 'day').format('YYYY-MM-DD') : null
    },
    canEdit () {
      return this.permissions.indexOf('UPDATE') > -1
    },
    canDelete () {
      return this.permissions.indexOf('DELETE') > -1
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    history () {
      return this.$store.getters.getProjectRateHistory
    },
    permissions () {
      return this.$store.getters.getPermissions.projectRateHistory
    }
  },
  methods: {
    blockTableSorting (tableHeaders) {
      tableHeaders.forEach(el => {
        el.sortable = false
      })
    },
    unblockTableSorting (tableHeaders) {
      tableHeaders.forEach(el => {
        if (el.value !== 'edit' && el.value !== 'data-table-expand') {
          el.sortable = true
        }
      })
    },
    async getPermissions () {
      await this.axios.get('/api/permissions/4', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setPermissions', {
          permissionsFor: 'projectRateHistory',
          permissions: res.data
        })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    openProjectRateDeleteDialog (item) {
      this.currentHistoryRate = item
      this.removeProjectRateDialog = true
    },
    cancelProjectRateDelete () {
      this.currentHistoryRate = null
      this.removeProjectRateDialog = false
    },
    deleteProjectRateHistory (deleteWithEmployeeRates) {
      let employeeRatesToDelete = null
      if (deleteWithEmployeeRates && this.currentHistoryRate.EmployeeRate.length) {
        employeeRatesToDelete = this.currentHistoryRate.EmployeeRate.map(el => el.Rate_ID)
      }
      this.axios.delete(`/api/project_rate_history/${this.currentHistoryRate.Project_Rate_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        },
        data: deleteWithEmployeeRates && employeeRatesToDelete ? employeeRatesToDelete : []
      }).then(response => {
        response.data.forEach(item => {
          item.From_Date = formatLocalDate(item.From_Date)
        })
        this.$store.dispatch('setRateHistoryForProject', {
          projectId: this.projectID,
          history: response.data
        })
        if (deleteWithEmployeeRates && employeeRatesToDelete) {
          this.getEmployeesRate()
        } else {
          this.projectRateHistory = this.mergeProjectEmployeeRate()
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Rate successfully deleted' })
        this.cancelProjectRateDelete()
        this.$emit('rateHistoryUpdated', { history: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    onEditProjRateClick (item) {
      if (this.editProjectRateMode) {
        this.updateProjectRate()
      } else {
        this.currentHistoryRate = Object.assign({}, item)
        const currentIndex = this.projectRateHistory.findIndex(el => el.Project_Rate_ID === this.currentHistoryRate.Project_Rate_ID)
        this.previousProjectRate = this.projectRateHistory[currentIndex - 1]
        this.nextProjectRate = this.projectRateHistory[currentIndex + 1]
        this.blockTableSorting(this.projectRateHeaders)
      }
      this.editProjectRateMode = !this.editProjectRateMode
    },
    cancelProjectRateEdit () {
      this.editProjectRateMode = false
      this.currentHistoryRate = null
      this.previousProjectRate = null
      this.nextProjectRate = null
      this.unblockTableSorting(this.projectRateHeaders)
    },
    updateProjectRate () {
      this.axios.put(`/api/project_rate_history/${this.currentHistoryRate.Project_Rate_ID}`, this.currentHistoryRate, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        response.data.forEach(item => {
          item.From_Date = formatLocalDate(item.From_Date)
        })
        this.$store.dispatch('setRateHistoryForProject', {
          projectId: this.projectID,
          history: response.data
        })
        this.projectRateHistory = this.mergeProjectEmployeeRate()
        this.$emit('rateHistoryUpdated', { history: response.data })
        this.cancelProjectRateEdit()
        this.$store.dispatch('setSuccess', { successMsg: 'Rate successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    addProjectRate () {
      if (this.addProjectRateMode) {
        if (this.previousProjectRate && this.previousProjectRate.EmployeeRate.length) {
          this.confirmRateCopyDialog = true
          return
        }
        this.postProjectRate(false)
      } else {
        let previousFromDate
        this.previousProjectRate = this.projectRateHistory[0]

        if (this.previousProjectRate && this.previousProjectRate.EmployeeRate.length) {
          previousFromDate = this.projectRateHistory[0].EmployeeRate[0].From_Date
        } else if (this.previousProjectRate && !this.previousProjectRate.EmployeeRate.length) {
          previousFromDate = this.projectRateHistory[0].From_Date
        }
        this.currentHistoryRate = {
          Project_Rate_ID: null,
          EmployeeRate: [],
          Project_ID: this.projectID,
          From_Date: previousFromDate ? moment(previousFromDate).add(1, 'day').format('YYYY-MM-DD') : formatLocalDate(new Date()),
          Rate: null
        }
      }
      this.addProjectRateMode = !this.addProjectRateMode
    },
    cancelProjectRateAdd () {
      this.addProjectRateMode = false
      this.previousProjectRate = null
      this.confirmRateCopyDialog = false
    },
    postProjectRate (saveWithEmployeeRate) {
      if (saveWithEmployeeRate && this.previousProjectRate && this.previousProjectRate.EmployeeRate.length) {
        this.currentHistoryRate.EmployeeRate = JSON.parse(JSON.stringify(this.previousProjectRate.EmployeeRate))
        for (const employeeRate of this.currentHistoryRate.EmployeeRate) {
          employeeRate.From_Date = this.currentHistoryRate.From_Date
        }
      }
      this.axios.post('/api/project_rate_history', this.currentHistoryRate, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        response.data.forEach(item => {
          item.From_Date = formatLocalDate(item.From_Date)
        })
        this.$store.dispatch('setRateHistoryForProject', {
          projectId: this.projectID,
          history: response.data
        })
        this.$emit('rateHistoryUpdated', { history: response.data })
        if (this.currentHistoryRate.EmployeeRate.length) {
          this.getEmployeesRate()
        } else {
          this.projectRateHistory = this.mergeProjectEmployeeRate()
        }
        this.cancelProjectRateAdd()
        this.$store.dispatch('setSuccess', { successMsg: 'Rate successfully added' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getEmployeesRate () {
      this.axios.get(`/api/project_employee_rate/${this.projectID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.allEmployeeRate = response.data
        this.projectRateHistory = this.mergeProjectEmployeeRate()
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    postEmployeeRate () {
      this.axios.post('/api/project_employee_rate/', this.currentEmployeeRate, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.allEmployeeRate = response.data
        this.projectRateHistory = this.mergeProjectEmployeeRate()
        this.addNewEmpRateMode = false
        this.onCancelEmpRateEdit()
        this.$store.dispatch('setSuccess', { successMsg: 'Employee rate successfully added' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    addNewEmployeeRate (item) {
      const indexExpanded = this.expanded.findIndex(el => el === item)
      if (indexExpanded === -1) {
        this.expanded.push(item)
      }
      this.currentEmployeeRate = {
        Rate_ID: -1,
        Employee_ID: null,
        Project_ID: this.projectID,
        From_Date: item.From_Date,
        Rate: null
      }
      this.blockTableSorting(this.employeeRateHeaders)
      this.currentHistoryRate = item
      this.currentHistoryRate.EmployeeRate.unshift(this.currentEmployeeRate)
      this.editEmpRateMode = true
      this.addNewEmpRateMode = true
    },
    onEditEmpRateClick (item, nestedItem) {
      if (this.editEmpRateMode && !this.addNewEmpRateMode) {
        this.updateEmployeeRate()
      } else if (this.editEmpRateMode && this.addNewEmpRateMode) {
        this.postEmployeeRate()
      } else {
        this.blockTableSorting(this.employeeRateHeaders)
        this.currentEmployeeRate = Object.assign({}, nestedItem)
        this.currentHistoryRate = item
      }
      this.editEmpRateMode = !this.editEmpRateMode
    },
    onCancelEmpRateEdit () {
      if (this.addNewEmpRateMode && this.currentEmployeeRate.Rate_ID === -1) {
        this.addNewEmpRateMode = false
        this.currentHistoryRate.EmployeeRate.shift()
      }
      this.editEmpRateMode = false
      this.searchEmployee = ''
      this.currentEmployeeRate = null
      this.currentHistoryRate = null
      this.unblockTableSorting(this.employeeRateHeaders)
    },
    updateEmployeeRate () {
      this.axios.put(`/api/project_employee_rate/${this.currentEmployeeRate.Rate_ID}`, this.currentEmployeeRate, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.allEmployeeRate = response.data
        this.projectRateHistory = this.mergeProjectEmployeeRate()
        this.onCancelEmpRateEdit()
        this.$store.dispatch('setSuccess', { successMsg: 'Employee rate successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    openEmployeeRateDeleteDialog (item, nestedItem) {
      this.currentEmployeeRate = nestedItem
      this.currentHistoryRate = item
      this.removeEmployeeRateDialog = true
    },
    cancelDeleteEmployeeRate () {
      this.currentEmployeeRate = null
      this.currentHistoryRate = null
      this.removeEmployeeRateDialog = false
    },
    deleteEmployeeRate () {
      this.axios.delete(`/api/project_employee_rate/${this.currentEmployeeRate.Rate_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.allEmployeeRate = response.data
        this.projectRateHistory = this.mergeProjectEmployeeRate()
        this.cancelDeleteEmployeeRate()
        this.$store.dispatch('setSuccess', { successMsg: 'Employee rate successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    mergeProjectEmployeeRate () {
      const copyOfHistory = this.history[this.projectID] ? JSON.parse(JSON.stringify(this.history[this.projectID])) : []
      copyOfHistory.forEach((historyItem) => {
        historyItem.EmployeeRate = this.allEmployeeRate.filter((employeeRate) => {
          const currentFromDate = moment(employeeRate.From_Date)
          const historyFromDate = moment(historyItem.From_Date)
          const nextHistoryItemIndex = copyOfHistory.indexOf(historyItem) + 1
          const nextHistoryItem = copyOfHistory[nextHistoryItemIndex]

          return currentFromDate.isSameOrAfter(historyFromDate) && (!nextHistoryItem || currentFromDate.isBefore(moment(nextHistoryItem.From_Date)))
        })
      })
      copyOfHistory.reverse()
      return copyOfHistory
    }
  }
}
</script>
